import { Group, InputWrapper, Select } from "@mantine/core";
import { getMonth, getYear, setMonth, setYear, startOfMonth } from "date-fns";
import { useEffect, useState } from "react";

export interface YearMonthSelectProps {
  value?: Date | null;
  onChange: (value: Date | null) => void;
  label?: string;
  error?: React.ReactNode;
  required?: boolean;
  description?: React.ReactNode;
  disabled?: boolean;
  // ...other InputWrapper props if needed
}

export function YearMonthSelect({
  value,
  onChange,
  label = "Year and month",
  error,
  required = true,
  description,
  disabled = false,
}: YearMonthSelectProps) {
  const currentYear = getYear(new Date());

  // Internal state for selects, derived from value
  const [internalYear, setInternalYear] = useState<string>(
    value ? getYear(value).toString() : "",
  );
  const [internalMonth, setInternalMonth] = useState<string>(
    value ? getMonth(value).toString() : "",
  );

  // Sync internal state with value prop
  useEffect(() => {
    if (value) {
      setInternalYear(getYear(value).toString());
      setInternalMonth(getMonth(value).toString());
    } else {
      setInternalYear("");
      setInternalMonth("");
    }
  }, [value]);

  const yearOptions = Array.from({ length: 31 }, (_, i) => {
    const year = currentYear - i;
    return { value: year.toString(), label: year.toString() };
  });

  const monthOptions = [
    { value: "0", label: "January" },
    { value: "1", label: "February" },
    { value: "2", label: "March" },
    { value: "3", label: "April" },
    { value: "4", label: "May" },
    { value: "5", label: "June" },
    { value: "6", label: "July" },
    { value: "7", label: "August" },
    { value: "8", label: "September" },
    { value: "9", label: "October" },
    { value: "10", label: "November" },
    { value: "11", label: "December" },
  ];

  // Handle select changes
  const handleYearChange = (year: string | null) => {
    setInternalYear(year ?? "");
    if (!year) {
      onChange(null);
      return;
    }
    if (internalMonth !== "") {
      // Both year and month selected
      const newDate = setYear(
        setMonth(startOfMonth(new Date()), parseInt(internalMonth)),
        parseInt(year),
      );
      onChange(newDate);
    } else {
      onChange(null);
    }
  };

  const handleMonthChange = (month: string | null) => {
    setInternalMonth(month ?? "");
    if (!month) {
      onChange(null);
      return;
    }
    if (internalYear !== "") {
      // Both year and month selected
      const newDate = setMonth(
        setYear(startOfMonth(new Date()), parseInt(internalYear)),
        parseInt(month),
      );
      onChange(newDate);
    } else {
      onChange(null);
    }
  };

  return (
    <InputWrapper
      label={label}
      error={error}
      required={required}
      description={description}
    >
      <Group grow>
        <Select
          data={yearOptions}
          value={internalYear}
          onChange={handleYearChange}
          disabled={disabled}
          allowDeselect={false}
        />
        <Select
          data={monthOptions}
          value={internalMonth}
          onChange={handleMonthChange}
          disabled={disabled}
          allowDeselect={false}
        />
      </Group>
    </InputWrapper>
  );
}
